import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSearchParams, Link } from 'react-router-dom'
import { Download, Filter, X, Package } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime, formatCurrency } from '../lib/utils'

// Phase 12.5 Phase 2 — Stock Ledger admin page.
//
// Two shapes in one page, controlled by `?product_id=<uuid>` in the URL:
//
//   • Scoped (product_id present) — full chronological history for ONE
//     SKU with a running-balance column. The ground truth for "what
//     happened to this bottle count over time, from any source". Linked
//     to from the Products list rows and from reconciliation reports.
//
//   • All-SKUs (no product_id) — filterable cross-SKU view for
//     reconciliation reports, shrinkage reviews, and "what moved in
//     the ledger today" questions from ops / Asad. Filters: date range,
//     reason, admin. Running-balance column is hidden here since it
//     would be nonsensical across SKUs.
//
// Running balance is computed client-side over the FULL fetched set
// (always returned ASC from the server) so filtering in-UI doesn't
// break the balance accumulation. Export CSV includes the exact rows
// currently shown, in current filter + sort order.

const REASON_CHIPS: { value: string; label: string; tone: 'in' | 'out' | 'neutral' }[] = [
  { value: 'initial_stock',       label: 'Initial',       tone: 'in'  },
  { value: 'baseline',            label: 'Baseline',      tone: 'neutral' },
  { value: 'po_receive',          label: 'PO receive',    tone: 'in'  },
  { value: 'sale',                label: 'Sale',          tone: 'out' },
  { value: 'return_from_customer',label: 'Return',        tone: 'in'  },
  { value: 'return_to_supplier',  label: 'Return supp.',  tone: 'out' },
  { value: 'void',                label: 'Void',          tone: 'in'  },
  { value: 'waste',               label: 'Waste',         tone: 'out' },
  { value: 'adjustment',          label: 'Adjustment',    tone: 'neutral' },
  { value: 'count_correction',    label: 'Count corr.',   tone: 'neutral' },
  { value: 'hold',                label: 'Hold',          tone: 'out' },
  { value: 'hold_released',       label: 'Hold rel.',     tone: 'in'  },
  { value: 'transfer_out',        label: 'Transfer out',  tone: 'out' },
  { value: 'reconciliation',      label: 'Reconciliation',tone: 'neutral' },
  { value: 'csv_import_adjust',   label: 'CSV adjust',    tone: 'neutral' },
]

interface Movement {
  id: string
  product_id?: string
  product_sku?: string | null
  product_name?: string | null
  delta_qty: number
  reason: string
  source_type: string | null
  source_id: string | null
  unit_cost_fils: number | null
  notes: string | null
  created_at: string
  created_by_admin_id: string | null
  admin_name: string | null
  order_number: string | null
  po_number: string | null
  stock_count_id?: string | null
  stock_count_posted_at?: string | null
  transfer_number: string | null
}

interface ProductMeta {
  id: string
  sku: string
  name_en: string
  stock: number
}

export default function StockLedger() {
  const [sp] = useSearchParams()
  const productId = sp.get('product_id') ?? ''

  // Scoped (single product) query — one round-trip for both the
  // header strip (sku / name / current stock) and the ledger rows.
  const scoped = useQuery<{ product: ProductMeta; movements: Movement[] }>({
    queryKey: ['stock-ledger-scoped', productId],
    queryFn:  () => api.get(`/products/${productId}/stock-movements`).then(r => r.data),
    enabled:  !!productId,
    staleTime: 5_000,
  })

  // All-SKUs filterable query
  const [since,   setSince]   = useState('')
  const [until,   setUntil]   = useState('')
  const [reasons, setReasons] = useState<Set<string>>(new Set())

  const allQueryKey = useMemo(
    () => ['stock-ledger-all', since, until, [...reasons].sort().join(',')],
    [since, until, reasons],
  )
  const all = useQuery<{ movements: Movement[]; limit: number }>({
    queryKey: allQueryKey,
    queryFn:  () => {
      const qp = new URLSearchParams()
      if (since) qp.set('since', since)
      if (until) qp.set('until', until)
      if (reasons.size > 0) qp.set('reason', [...reasons].join(','))
      qp.set('limit', '500')
      return api.get(`/products/stock-movements/all?${qp.toString()}`).then(r => r.data)
    },
    enabled: !productId,
    staleTime: 5_000,
  })

  function toggleReason(r: string) {
    setReasons(prev => {
      const next = new Set(prev)
      if (next.has(r)) next.delete(r); else next.add(r)
      return next
    })
  }

  function exportCsv(rows: Movement[], runningBalances?: number[]) {
    const header = productId
      ? ['timestamp', 'delta', 'running_balance', 'reason', 'source', 'source_ref', 'unit_cost_jd', 'admin', 'notes']
      : ['timestamp', 'sku', 'product', 'delta', 'reason', 'source', 'source_ref', 'unit_cost_jd', 'admin', 'notes']
    const lines = [header.join(',')]
    rows.forEach((m, i) => {
      const sourceRef = m.order_number ?? m.po_number ?? m.transfer_number ?? (m.source_id ?? '')
      const unitCost = m.unit_cost_fils != null ? (m.unit_cost_fils / 1000).toFixed(3) : ''
      const notes = (m.notes ?? '').replace(/"/g, '""')
      const cells = productId
        ? [m.created_at, String(m.delta_qty), runningBalances?.[i] != null ? String(runningBalances[i]) : '',
           m.reason, m.source_type ?? '', sourceRef, unitCost, m.admin_name ?? '', `"${notes}"`]
        : [m.created_at, m.product_sku ?? '', m.product_name ?? '',
           String(m.delta_qty), m.reason, m.source_type ?? '', sourceRef, unitCost, m.admin_name ?? '', `"${notes}"`]
      lines.push(cells.join(','))
    })
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    const fname = productId
      ? `stock-ledger-${scoped.data?.product?.sku ?? 'sku'}-${new Date().toISOString().slice(0, 10)}.csv`
      : `stock-ledger-all-${new Date().toISOString().slice(0, 10)}.csv`
    a.download = fname
    a.click()
    URL.revokeObjectURL(url)
  }

  // ── Scoped (single-SKU) view ────────────────────────────────────────
  if (productId) {
    const movements = scoped.data?.movements ?? []
    // Running balance accumulated in ASC order (as returned), then
    // reversed for display (newest first on screen).
    const running: number[] = []
    let bal = 0
    for (const m of movements) { bal += m.delta_qty; running.push(bal) }
    const displayRows = [...movements].reverse()
    const displayRunning = [...running].reverse()
    const product = scoped.data?.product

    return (
      <div className="p-6 max-w-6xl mx-auto space-y-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <Link to="/stock-ledger" className="text-xs text-gray-500 hover:text-white">← All movements</Link>
            <h1 className="text-xl font-semibold text-white mt-2 flex items-center gap-2">
              <Package size={18} className="text-[#C41E3A]" />
              Stock ledger
            </h1>
            {product && (
              <div className="mt-1 text-sm text-gray-400">
                <span className="font-mono text-xs text-gray-500">{product.sku}</span>
                <span className="mx-2">·</span>
                <span className="text-white">{product.name_en}</span>
                <span className="mx-2 text-gray-600">·</span>
                <span className="text-gray-300">Current stock: <span className="font-semibold text-white">{product.stock}</span></span>
              </div>
            )}
          </div>
          <button
            onClick={() => exportCsv(displayRows, displayRunning)}
            disabled={displayRows.length === 0}
            className="inline-flex items-center gap-1.5 bg-[#242424] hover:bg-[#2e2e2e] border border-[#3a3a3a] text-white text-xs rounded-lg px-3 py-1.5 disabled:opacity-40"
          >
            <Download size={13} /> Export CSV
          </button>
        </div>

        {scoped.isLoading && <p className="text-sm text-gray-500">Loading ledger…</p>}
        {!scoped.isLoading && displayRows.length === 0 && (
          <p className="text-sm text-gray-500">No ledger movements for this SKU yet.</p>
        )}

        {displayRows.length > 0 && (
          <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#111] text-xs text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">When</th>
                  <th className="text-right px-3 py-2">Δ</th>
                  <th className="text-right px-3 py-2">Running</th>
                  <th className="text-left px-3 py-2">Reason</th>
                  <th className="text-left px-3 py-2">Source</th>
                  <th className="text-right px-3 py-2">Unit cost</th>
                  <th className="text-left px-3 py-2">By</th>
                  <th className="text-left px-3 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {displayRows.map((m, i) => (
                  <MovementRow key={m.id} m={m} runningBalance={displayRunning[i]} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    )
  }

  // ── All-SKUs filterable view ────────────────────────────────────────
  const movements = all.data?.movements ?? []
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-white flex items-center gap-2">
            <Package size={18} className="text-[#C41E3A]" />
            Stock ledger — all movements
          </h1>
          <p className="text-xs text-gray-500 mt-1">
            Every stock-changing event across the catalog. Pick a product row to drill into its full history and running balance.
          </p>
        </div>
        <button
          onClick={() => exportCsv(movements)}
          disabled={movements.length === 0}
          className="inline-flex items-center gap-1.5 bg-[#242424] hover:bg-[#2e2e2e] border border-[#3a3a3a] text-white text-xs rounded-lg px-3 py-1.5 disabled:opacity-40"
        >
          <Download size={13} /> Export CSV
        </button>
      </div>

      <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl p-4 space-y-3">
        <div className="flex items-center gap-2 text-xs text-gray-500">
          <Filter size={13} /> Filters
          {(since || until || reasons.size > 0) && (
            <button
              onClick={() => { setSince(''); setUntil(''); setReasons(new Set()) }}
              className="ml-auto text-gray-400 hover:text-white inline-flex items-center gap-1"
            >
              <X size={11} /> Clear
            </button>
          )}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <label className="text-xs text-gray-400">
            Since
            <input
              type="date"
              value={since}
              onChange={e => setSince(e.target.value)}
              className="mt-1 w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            />
          </label>
          <label className="text-xs text-gray-400">
            Until (exclusive)
            <input
              type="date"
              value={until}
              onChange={e => setUntil(e.target.value)}
              className="mt-1 w-full bg-[#111] border border-[#2e2e2e] rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-[#C41E3A]"
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-1.5 pt-1">
          {REASON_CHIPS.map(r => {
            const on = reasons.has(r.value)
            return (
              <button
                key={r.value}
                onClick={() => toggleReason(r.value)}
                className={`text-xs rounded-full px-2.5 py-1 border transition-colors ${
                  on
                    ? 'bg-[#C41E3A]/20 border-[#C41E3A]/60 text-[#f4a0b1]'
                    : 'bg-[#111] border-[#2e2e2e] text-gray-400 hover:border-[#444]'
                }`}
              >
                {r.label}
              </button>
            )
          })}
        </div>
      </div>

      {all.isLoading && <p className="text-sm text-gray-500">Loading ledger…</p>}
      {!all.isLoading && movements.length === 0 && (
        <p className="text-sm text-gray-500">No ledger movements match the current filters.</p>
      )}

      {movements.length > 0 && (
        <div className="bg-[#1a1a1a] border border-[#2e2e2e] rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#111] text-xs text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2">When</th>
                <th className="text-left px-3 py-2">SKU</th>
                <th className="text-left px-3 py-2">Product</th>
                <th className="text-right px-3 py-2">Δ</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-left px-3 py-2">Source</th>
                <th className="text-left px-3 py-2">By</th>
                <th className="text-left px-3 py-2">Notes</th>
              </tr>
            </thead>
            <tbody>
              {movements.map(m => <MovementRow key={m.id} m={m} crossSku />)}
            </tbody>
          </table>
          {movements.length >= (all.data?.limit ?? 500) && (
            <p className="text-xs text-amber-500 bg-amber-500/5 border-t border-[#2e2e2e] px-3 py-2">
              Showing the first {all.data?.limit} rows. Tighten the date range or reason filters to see more.
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function MovementRow({ m, runningBalance, crossSku }: { m: Movement; runningBalance?: number; crossSku?: boolean }) {
  const reasonChip = REASON_CHIPS.find(r => r.value === m.reason)
  const toneCls = reasonChip?.tone === 'in'  ? 'text-emerald-400 bg-emerald-400/10'
                : reasonChip?.tone === 'out' ? 'text-rose-400 bg-rose-400/10'
                :                              'text-gray-300 bg-white/5'
  const deltaCls = m.delta_qty > 0 ? 'text-emerald-400' : m.delta_qty < 0 ? 'text-rose-400' : 'text-gray-500'
  const sourceLabel = m.order_number     ? `Order ${m.order_number}`
                    : m.po_number        ? `PO ${m.po_number}`
                    : m.transfer_number  ? `Transfer ${m.transfer_number}`
                    : m.source_type === 'stock_count'   ? 'Stock count'
                    : m.source_type === 'product_edit'  ? 'Admin edit'
                    : m.source_type === 'product_create' ? 'Product create'
                    : m.source_type === 'product_import' ? 'CSV import'
                    : m.source_type === 'manual' ? 'Manual'
                    : m.source_type ?? '—'
  const sourceHref = m.order_number ? `/orders?open=${m.source_id}`
                   : m.po_number    ? `/purchase-orders?open=${m.source_id}`
                   : null

  return (
    <tr className="border-t border-[#222] hover:bg-white/[0.02]">
      <td className="px-3 py-2 text-xs text-gray-400 whitespace-nowrap">{formatDateTime(m.created_at)}</td>
      {crossSku && (
        <>
          <td className="px-3 py-2 text-xs font-mono text-gray-400">{m.product_sku ?? '—'}</td>
          <td className="px-3 py-2 text-xs">
            {m.product_id ? (
              <Link to={`/stock-ledger?product_id=${m.product_id}`} className="text-white hover:text-[#C41E3A]">
                {m.product_name ?? '—'}
              </Link>
            ) : (m.product_name ?? '—')}
          </td>
        </>
      )}
      <td className={`px-3 py-2 text-right font-mono text-sm ${deltaCls}`}>
        {m.delta_qty > 0 ? '+' : ''}{m.delta_qty}
      </td>
      {!crossSku && (
        <td className="px-3 py-2 text-right font-mono text-sm text-gray-300">
          {runningBalance ?? '—'}
        </td>
      )}
      <td className="px-3 py-2">
        <span className={`text-xs rounded-full px-2 py-0.5 ${toneCls}`}>
          {reasonChip?.label ?? m.reason}
        </span>
      </td>
      <td className="px-3 py-2 text-xs text-gray-300">
        {sourceHref ? (
          <Link to={sourceHref} className="text-[#C41E3A] hover:underline">{sourceLabel}</Link>
        ) : sourceLabel}
      </td>
      {!crossSku && (
        <td className="px-3 py-2 text-right text-xs font-mono text-gray-500">
          {m.unit_cost_fils != null ? formatCurrency(m.unit_cost_fils) : '—'}
        </td>
      )}
      <td className="px-3 py-2 text-xs text-gray-400">{m.admin_name ?? '—'}</td>
      <td className="px-3 py-2 text-xs text-gray-500 max-w-[320px] truncate" title={m.notes ?? ''}>
        {m.notes ?? '—'}
      </td>
    </tr>
  )
}
